import { Fragment, useContext } from "react";

import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { IconButton } from "@/DesignSystem";
import { CareComponentConfigurationContext } from "@/Flowsheets/Models/CareComponentConfigurationContext";
import { CareElementConfig } from "@/Flowsheets/Models/CareElements/CareElementConfig";

import styles from "./CareElementsConfigurationGrid.module.scss";

import X from "@/DesignSystem/Atoms/Icons/Types/X/x-regular.svg";

type CareElementConfigColumns = {
    label: string;
    render: (element: CareElementConfig, careComponentName: string) => JSX.Element;
    getKey: (id: string) => string;
};

interface CareElementsConfigurationGridProps {
    careElementsColumns?: CareElementConfigColumns[];
}

function CareElementsConfigurationGrid({ careElementsColumns }: Readonly<CareElementsConfigurationGridProps>): JSX.Element {
    const { getText } = useTranslation();
    const { careComponentName, careElements, onRemove } = useContext(CareComponentConfigurationContext);
    const columns = careElementsColumns ?? [];

    if (careElements?.length === 0)
        return <></>;

    return (
        <Fragment>
            <p className={`${styles.tableHeader} ${styles.careElementHeader}`}>{getText("Flowsheets_Configuration_CareElementLabel")}</p>

            {columns.map((column, index) => (
                <p key={`header-${column.getKey(index.toString())}`} className={styles.tableHeader}>{column.label}</p>
            ))}

            {careElements.map(element => {
                const name = element.Name.replaceAll(" ", "").replaceAll("/", "");
                const label = `Flowsheets_${careComponentName}Label_${name}`;
                return (
                    <Fragment key={`row-${element.Name}`}>
                        <IconButton
                            id={styles.removeButton}
                            dataCy="removeButtonIcon"
                            size="sm"
                            variant="error"
                            category="tertiary"
                            onClick={() => onRemove(element.Name)}
                        >
                            <X />
                        </IconButton>

                        <span className={styles.careElementColumn}>
                            {getText(label)}
                        </span>

                        {columns.map(column => (
                            <Fragment key={column.getKey(element.Name)}>
                                {column.render(element, careComponentName)}
                            </Fragment>
                        ))}
                    </Fragment>
                );
            })}
        </Fragment>
    );
}

export { CareElementConfigColumns, CareElementsConfigurationGrid };
